import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { generateEHCNAContent, exportEhcnaPdf } from '../../lib/functions';
import { getStudentEvidenceBundleV2 } from '../../lib/api';
import { useStudents } from '../../lib/useStudents';
import { useSchoolId } from '../../lib/auth/schoolContext';
import { studentHasEhcp } from '../../lib/students';
import SchoolPageHeader from '../../components/school/SchoolPageHeader';
import CapabilityNotice from '../../components/school/CapabilityNotice';
import StudentSelector from './components/StudentSelector';
import { Icons } from '../../lib/Icons';
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../../lib/auth/capabilities';
import {
  assessEvidenceReadiness,
  eligibilityMessage,
  formatEvidenceSummary,
  planDoReviewFromApdrSupportPlan,
} from './lib/evidenceReadiness';
import { fetchCostedProvisionForStudent } from './lib/provisionCost';
import EhcnaDraftReport from './components/EhcnaDraftReport';
import { extractCallableContent } from './lib/formatDraftContent';
import {
  buildFallbackAssessSummary,
  buildFallbackJourneyNarrative,
  buildFallbackSmartOutcomes,
} from './lib/fallbackDraft';

const STEP_LABELS = [
  'Load Evidence Bundle',
  'Graduated Response Check',
  'Costed Provision Summary',
  'Generate Draft',
];

export default function EHCNAWizardPage() {
  const { requestId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { schoolId } = useSchoolId();
  const { can } = useSchoolCapabilities();
  const { students } = useStudents({ orderByLastName: true, ensureSubscription: true });

  const initialStudentId = searchParams.get('studentId') || '';
  const [lockedFromIsp] = useState(() => Boolean(initialStudentId));
  const [step, setStep] = useState(1);
  const [studentId, setStudentId] = useState(initialStudentId);
  const [notes, setNotes] = useState('');
  const [costedProvisionSummary, setCostedProvisionSummary] = useState('');
  const [costedProvisionRows, setCostedProvisionRows] = useState([]);
  const [evidenceBundle, setEvidenceBundle] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === studentId) || null,
    [students, studentId]
  );

  const studentName = selectedStudent
    ? `${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim()
    : '';

  const pageTitle = useMemo(
    () => (requestId ? `EHCNA Request ${requestId}` : studentName ? `EHCNA: ${studentName}` : 'New EHCNA Request'),
    [requestId, studentName]
  );

  const handleStudentChange = (id) => {
    if (lockedFromIsp) return;
    setStudentId(id);
    setEvidenceBundle(null);
    setReadiness(null);
    setCostedProvisionSummary('');
    setCostedProvisionRows([]);
    setGenerated(null);
    setStep(1);
    if (id) {
      setSearchParams({ studentId: id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const loadEvidence = useCallback(async () => {
    if (!studentId || !schoolId) return null;
    setLoadingEvidence(true);
    setError('');
    try {
      const [bundleResult, provision] = await Promise.all([
        getStudentEvidenceBundleV2({ studentId }),
        fetchCostedProvisionForStudent({ studentId, schoolId }),
      ]);

      const bundle = bundleResult?.bundle || null;
      const sessionLogCount = bundleResult?.readiness?.sessionLogCount ?? 0;
      const assessment = assessEvidenceReadiness(bundle, sessionLogCount);

      setEvidenceBundle(bundle);
      setReadiness(assessment);
      setCostedProvisionRows(provision.rows);
      setCostedProvisionSummary(provision.summary);
      return assessment;
    } catch (e) {
      setError(e?.message || 'Unable to load evidence for this student.');
      setEvidenceBundle(null);
      setReadiness(null);
      return null;
    } finally {
      setLoadingEvidence(false);
    }
  }, [studentId, schoolId]);

  useEffect(() => {
    if (studentId && schoolId) {
      loadEvidence();
    }
  }, [studentId, schoolId, loadEvidence]);

  const runStep = async () => {
    if (!studentId) return;
    setBusy(true);
    setError('');

    try {
      if (step === 1) {
        const assessment = await loadEvidence();
        if (!assessment) return;
        if (assessment.hasEHCP || studentHasEhcp(selectedStudent)) {
          setError('This student already has an EHCP. EHCNA is only for students without a plan.');
          return;
        }
        setStep(2);
      } else if (step === 2) {
        const assessment = readiness || (await loadEvidence());
        if (!assessment?.canProceed) {
          setError('Evidence is not yet strong enough to proceed. Build more graduated response documentation first.');
          return;
        }
        setStep(3);
      } else if (step === 3) {
        if (!costedProvisionSummary && (!costedProvisionRows?.length)) {
          setError('No costed provision found. Add student provision or interventions with staff costs first.');
          return;
        }
        setStep(4);
      } else {
        const [journeySettled, outcomesSettled, assessSettled] = await Promise.allSettled([
          generateEHCNAContent({ type: 'journey_narrative', studentId }),
          generateEHCNAContent({ type: 'smart_outcomes', studentId }),
          generateEHCNAContent({ type: 'assess_summary', studentId }),
        ]);

        const journeyFromAi =
          journeySettled.status === 'fulfilled' ? extractCallableContent(journeySettled.value) : null;
        const outcomesFromAi =
          outcomesSettled.status === 'fulfilled' ? extractCallableContent(outcomesSettled.value) : null;
        const assessFromAi =
          assessSettled.status === 'fulfilled' ? extractCallableContent(assessSettled.value) : null;

        const aiErrors = [journeySettled, outcomesSettled, assessSettled]
          .filter((r) => r.status === 'rejected')
          .map((r) => r.reason?.message || 'Generation failed');

        const bundleForFallback = {
          ...evidenceBundle,
          sessionLogCount: readiness?.counts?.sessionLogCount ?? evidenceBundle?.sessionLogCount,
          apdrCycleCount: readiness?.counts?.apdrCycleCount ?? evidenceBundle?.apdrCycleCount,
        };

        const journeyNarrative =
          journeyFromAi || buildFallbackJourneyNarrative(bundleForFallback, studentName);
        const smartOutcomes =
          outcomesFromAi || buildFallbackSmartOutcomes(bundleForFallback);
        const assessSummary =
          assessFromAi || buildFallbackAssessSummary(bundleForFallback);

        if (aiErrors.length) {
          setError(
            `Some AI sections could not be generated (${aiErrors[0]}). An evidence-based draft has been filled in below. Review before submission.`
          );
          toast.error('AI draft partially unavailable. Using evidence-based fallback');
        } else if (!journeyFromAi && !outcomesFromAi && !assessFromAi) {
          toast.success('Draft built from your evidence bundle (AI returned no text)');
        } else {
          toast.success('EHCNA draft generated');
        }

        setGenerated({
          journeyNarrative,
          smartOutcomes,
          assessSummary,
          notes,
          costedProvisionSummary,
          costedProvisionRows,
          planDoReview: planDoReviewFromApdrSupportPlan(evidenceBundle?.apdrSupportPlan),
          readiness,
          usedAi: Boolean(journeyFromAi || outcomesFromAi || assessFromAi),
        });
      }
    } catch (e) {
      setError(e?.message || 'Unable to complete this step right now.');
    } finally {
      setBusy(false);
    }
  };

  const evidenceDisplay = evidenceBundle && readiness
    ? formatEvidenceSummary(evidenceBundle, readiness)
    : loadingEvidence
      ? 'Loading evidence from interventions, targets, and reports…'
      : 'Select a student to load their evidence bundle';

  const handleDownloadPdf = async () => {
    if (!studentId || !generated) return;
    setDownloadingPdf(true);
    const toastId = toast.loading('Generating EHCNA PDF…');
    try {
      const totalAnnualCost = (generated.costedProvisionRows || []).reduce(
        (sum, r) => sum + Number(r.totalAnnual || 0),
        0
      );
      const { data } = await exportEhcnaPdf({
        studentId,
        draft: {
          journeyNarrative: generated.journeyNarrative || undefined,
          smartOutcomes: generated.smartOutcomes || undefined,
          assessSummary: generated.assessSummary || undefined,
          notes: generated.notes || undefined,
          costedProvisionRows: generated.costedProvisionRows,
          totalAnnualCost,
        },
      });
      if (!data?.downloadUrl) {
        throw new Error('No download URL returned');
      }
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.filename || `EHCNA-${studentName || studentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('EHCNA PDF downloaded', { id: toastId });
    } catch (e) {
      toast.error(e?.message || 'Could not generate EHCNA PDF', { id: toastId });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const draftContent = useMemo(
    () => ({
      journeyNarrative: generated?.journeyNarrative ?? null,
      smartOutcomes: generated?.smartOutcomes ?? null,
      assessSummary: generated?.assessSummary ?? null,
      notes: (generated?.notes ?? notes) || '',
      costedProvisionSummary,
      costedProvisionRows,
      planDoReview: planDoReviewFromApdrSupportPlan(evidenceBundle?.apdrSupportPlan),
      readiness,
    }),
    [generated, notes, costedProvisionSummary, costedProvisionRows, evidenceBundle, readiness]
  );

  const hasDraftPreview = Boolean(
    studentId
    && (costedProvisionRows?.length > 0
      || evidenceBundle?.apdrSupportPlan
      || generated)
  );

  const eligibilityDisplay = readiness
    ? eligibilityMessage(readiness)
    : studentId
      ? 'Checking eligibility…'
      : 'Select a student to run the eligibility check';

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <SchoolPageHeader
        title={pageTitle}
        description="Guided flow: evidence readiness → graduated response check → costed provision → AI-assisted EHCNA draft."
        icon={Icons.ClipboardDocumentCheck}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'EHCNA', href: '/ehcna' },
          { label: requestId ? 'Request' : studentName || 'New' },
        ]}
      />

      {!can(SCHOOL_ACTIONS.GENERATE_EHCNA) && (
        <CapabilityNotice
          title="SENCO action required"
          body="Only SENCO users can run end-to-end EHCNA generation. Teachers can review outputs once created."
        />
      )}

      {selectedStudent && studentHasEhcp(selectedStudent) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {studentName} already has an EHCP.{' '}
          <Link to={`/student/${studentId}`} className="font-medium underline">
            View their profile
          </Link>{' '}
          to manage EHCP evidence instead.
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm text-slate-500">
          <span className="rounded-full bg-indigo-50 px-2 py-1 text-indigo-700">Step {step} of 4</span>
          <span>{STEP_LABELS[step - 1]}</span>
        </div>

        <div className="space-y-4">
          <div>
            <span className="mb-2 block text-sm font-medium text-slate-700">Student</span>
            {lockedFromIsp && selectedStudent ? (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-3">
                <p className="font-medium text-slate-900">{studentName || 'Pupil'}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {selectedStudent.yearGroup || 'Year group not set'}
                  {' · '}
                  {selectedStudent.primaryNeed || selectedStudent.senStatus || 'SEN Support'}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Opened from this pupil&apos;s ISP. The pupil is fixed for this request.
                </p>
                <Link
                  to={`/student/${studentId}`}
                  className="mt-2 inline-block text-sm text-indigo-600 hover:underline"
                >
                  Open ISP profile
                </Link>
              </div>
            ) : (
              <>
                <StudentSelector value={studentId} onChange={handleStudentChange} disabled={busy} />
                {selectedStudent && (
                  <p className="mt-2 text-sm text-slate-600">
                    {selectedStudent.yearGroup} · {selectedStudent.primaryNeed || 'SEN Support'}
                    {' · '}
                    <Link to={`/student/${studentId}`} className="text-indigo-600 hover:underline">
                      Open ISP profile
                    </Link>
                  </p>
                )}
              </>
            )}
          </div>

          {step >= 2 && readiness && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
              <p className="font-medium text-slate-900">Graduated response summary</p>
              <p className="mt-1 text-slate-600">
                Evidence strength:{' '}
                <span className={`font-medium ${
                  readiness.strength === 'strong' ? 'text-green-700' :
                  readiness.strength === 'moderate' ? 'text-amber-700' : 'text-red-700'
                }`}>
                  {readiness.strength}
                </span>
                {' '}({readiness.percentage}%)
              </p>
              {readiness.gaps.length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-600">
                  {readiness.gaps.map((gap) => (
                    <li key={gap}>{gap}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Case notes (optional)</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
              placeholder="Additional context for the LA. Use non-diagnostic language"
            />
          </label>

          {step >= 3 && costedProvisionSummary && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-900">Costed provision (from intervention map)</p>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-700">{costedProvisionSummary}</pre>
            </div>
          )}
        </div>

        {error && <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            disabled={busy || loadingEvidence || !studentId || (step === 2 && !readiness?.canProceed) || (step === 4 && !can(SCHOOL_ACTIONS.GENERATE_EHCNA))}
            onClick={runStep}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Working…' : step < 4 ? (step === 1 && loadingEvidence ? 'Loading evidence…' : 'Continue') : 'Generate Draft'}
          </button>
          {step > 1 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setStep((prev) => Math.max(prev - 1, 1))}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Back
            </button>
          )}
          {studentId && (
            <button
              type="button"
              disabled={loadingEvidence}
              onClick={loadEvidence}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Refresh evidence
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <ResultCard title="Evidence Bundle" value={evidenceDisplay} pre />
        <ResultCard
          title="EHCNA Eligibility Check"
          value={eligibilityDisplay}
          tone={readiness?.canProceed ? 'ok' : readiness?.hasEHCP ? 'warn' : 'muted'}
        />
        <ResultCard
          title="Costed Provision Notes"
          value={costedProvisionSummary || (studentId ? 'No costed interventions found. Assign staff to interventions on the student profile.' : 'Select a student first')}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">Generated EHCNA Draft</h2>
          {generated && can(SCHOOL_ACTIONS.GENERATE_EHCNA) && (
            <button
              type="button"
              disabled={downloadingPdf}
              onClick={handleDownloadPdf}
              className="rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-800 disabled:opacity-50"
            >
              {downloadingPdf ? 'Generating PDF…' : 'Download PDF'}
            </button>
          )}
        </div>
        {!hasDraftPreview ? (
          <p className="mt-3 text-sm text-slate-600">Select a student to load provision and evidence into the draft preview.</p>
        ) : (
          <>
            {!generated && step < 4 && (
              <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <strong>Preview:</strong> costed provision and graduated response appear below as you load evidence.
                Continue to <strong>step 4</strong> and click <strong>Generate Draft</strong> for AI-written journey, assess, and outcomes sections.
              </p>
            )}
            {!generated && step === 4 && (
              <p className="mt-3 rounded-md bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
                Click <strong>Generate Draft</strong> above to complete narrative sections. The preview below already includes your costed provision map.
              </p>
            )}
            {generated && !generated.usedAi && (
              <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Draft built from school evidence (AI narrative was unavailable). Review and edit before LA submission.
              </p>
            )}
            <EhcnaDraftReport generated={draftContent} studentName={studentName} />
          </>
        )}
      </div>
    </div>
  );
}

function ResultCard({ title, value, pre = false, tone = 'muted' }) {
  const toneClass = tone === 'ok'
    ? 'text-green-800'
    : tone === 'warn'
      ? 'text-amber-800'
      : 'text-slate-600';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {pre ? (
        <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-slate-50 p-2 text-xs text-slate-700">{value}</pre>
      ) : (
        <p className={`mt-2 break-words text-sm ${toneClass}`}>{value}</p>
      )}
    </div>
  );
}
